package nl.volleyplanner.api.client.planner

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import nl.volleyplanner.api.entities.OrganisationSeason
import nl.volleyplanner.api.planner.*

class PlannerConfigurationEndpoint(val httpClient: HttpClient, prefix: String) {
    val base = "$prefix/planner"

    val seasonalEndpoints = mutableMapOf<OrganisationSeason, PlannerConfigurationSeasonalEndpoint>()
    fun getSeasonalEndpoint(season: OrganisationSeason) = seasonalEndpoints.getOrPut(season, { PlannerConfigurationSeasonalEndpoint(httpClient, base, season) })

    val problemVersionTaskEndpoints = mutableMapOf<Pair<String, String?>, TasksClientEndpoint>()
    fun getTasksEndpoint(problemSlug: String, problemVersionSlug: String? = null) =
        problemVersionTaskEndpoints.getOrPut(Pair(problemSlug, problemVersionSlug), {
            val prefix = if (problemVersionSlug == null) {
                "$base/planning/$problemSlug"
            } else {
                "$base/planning/$problemSlug/versions/$problemVersionSlug"
            }
            TasksClientEndpoint(httpClient, prefix)
        })

    suspend fun getPlannerPrerequisites() : PlannerPrerequisites {
        return httpClient.get("$base/prerequisites").body()
    }

    suspend fun listPlannerConfigurations() : List<PlannerConstraintConfiguration> {
        return httpClient.get("$base/configurations/list").body()
    }

    suspend fun createPlannerConfiguration(createConfigurationRequest : CreatePlannerConstraintConfigurationRequest) : PlannerConstraintConfiguration {
        return httpClient.post("$base/configurations/create") {
            contentType(ContentType.Application.Json)
            setBody(createConfigurationRequest)
        }.body()
    }

    suspend fun getPlannerConfiguration(configurationIdentifier: String) : PlannerConstraintConfiguration {
        return httpClient.get("$base/configurations/$configurationIdentifier").body()
    }

    suspend fun updatePlannerConfiguration(configuration : PlannerConstraintConfiguration) {
        httpClient.post("$base/configurations/${configuration.identifier}") {
            contentType(ContentType.Application.Json)
            setBody(configuration)
        }
    }

    /**
     * Planner constraint configuration
     */
    suspend fun getConstraintConfiguration() : PlannerConstraintConfiguration {
        return httpClient.get("$base/constraints").body()
    }

    suspend fun updateConstraintConfiguration(configuration : PlannerConstraintConfiguration) {
        httpClient.post("$base/constraints/update") {
            contentType(ContentType.Application.Json)
            setBody(configuration)
        }
    }

    /**
     * Planning problems
     */
    suspend fun listProblems(includeArchived: Boolean) : List<PlanningProblem> {
        return httpClient.get("$base/planning/list") {
            url {
                parameters.append("archived", includeArchived.toString())
            }
        }.body()
    }

    suspend fun getProblemBySlug(slug: String) : PlanningProblem? {
        return httpClient.get("$base/planning/$slug").body()
    }

    @Deprecated("Not sure if we want to use this")
    suspend fun updateProblem(problem: PlanningProblem) {
        httpClient.put("$base/planning/${problem.identifier}") {
            contentType(ContentType.Application.Json)
            setBody(problem)
        }
    }

    suspend fun listProblemVersions(problemSlug: String) : List<PlanningProblemVersion> {
        return httpClient.get("$base/planning/$problemSlug/versions/list").body()
    }

    suspend fun getProblemVersion(problemSlug: String, versionSlug: String) : PlanningProblemVersion {
        return httpClient.get("$base/planning/$problemSlug/versions/$versionSlug").body()
    }

    suspend fun getChangesBehindPublished(problemSlug: String, versionSlug: String) : List<Task> {
        return httpClient.get("$base/planning/$problemSlug/versions/$versionSlug/changes-behind").body()
    }

    suspend fun mergeChangesBehindPublished(problemSlug: String, versionSlug: String, taskIdsToMerge: Set<String>) {
        httpClient.post("$base/planning/$problemSlug/versions/$versionSlug/merge-published") {
            contentType(ContentType.Application.Json)
            setBody(MergePublishedToVersionRequest(taskIdsToMerge))
        }
    }

    suspend fun createProblemVersion(problemSlug: String, version: CreatePlanningProblemVersionRequest) : PlanningProblemVersion {
        return httpClient.post("$base/planning/$problemSlug/versions/create") {
            contentType(ContentType.Application.Json)
            setBody(version)
        }.body()
    }

    suspend fun updateProblemVersion(request: UpdatePlanningProblemVersionRequest) {
        val versionPath = if (request.problemVersionSlug != null) {
            "/versions/${request.problemVersionSlug}"
        } else ""
        httpClient.post("$base/planning/${request.problemSlug}${versionPath}/update") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
    }

    suspend fun publishProblemVersion(problemSlug: String, versionSlug: String) {
        httpClient.post("$base/planning/$problemSlug/versions/$versionSlug/publish")
    }
}

class PlannerConfigurationSeasonalEndpoint(val httpClient: HttpClient, prefix: String, val organisationSeason: OrganisationSeason) {
    private val seasonId = organisationSeason.name
    val base = "$prefix/$seasonId"

    /**
     * Rulesets
     */
    suspend fun listSupportedRefereeGroupConstraints() : List<GroupConstraint> {
        return httpClient.get("$base/group-constraints/referee/list").body()
    }

    suspend fun listSupportedScoreKeeperGroupConstraints() : List<GroupConstraint> {
        return httpClient.get("$base/group-constraints/score-keeper/list").body()
    }

    suspend fun listRulesets() : List<Ruleset> {
        return httpClient.get("$base/rulesets/list").body()
    }

    suspend fun getRuleset(id: String) : Ruleset? {
        return httpClient.get("$base/rulesets/$id").body()
    }

    suspend fun deleteRuleset(id: String) {
        httpClient.delete("$base/rulesets/$id")
    }

    /**
     * Team/poule configs
     */
    suspend fun listTeamPouleConfigs() : List<TeamPouleConfiguration> {
        return httpClient.get("$base/team_poule_configs/list").body()
    }
}

class HeavyPlannerConfigurationEndpoint(val httpClient: HttpClient, prefix: String) {
    val base = "$prefix/planner"

    val seasonalEndpoints = mutableMapOf<OrganisationSeason, HeavyPlannerConfigurationSeasonalEndpoint>()
    fun getSeasonalEndpoint(season: OrganisationSeason) = seasonalEndpoints.getOrPut(season, { HeavyPlannerConfigurationSeasonalEndpoint(httpClient, base, season) })
}

class HeavyPlannerConfigurationSeasonalEndpoint(val httpClient: HttpClient, prefix: String, val organisationSeason: OrganisationSeason) {
    private val seasonId = organisationSeason.name
    val base = "$prefix/$seasonId"

    suspend fun createDefaultRulesets() {
        httpClient.post("$base/rulesets/create-defaults")
    }

    suspend fun bulkCreateOrUpdateRulesets(rulesets: List<Ruleset>) {
        httpClient.post("$base/rulesets/update") {
            contentType(ContentType.Application.Json)
            setBody(rulesets)
        }
    }

    suspend fun createOrUpdateRuleset(ruleset: Ruleset) {
        httpClient.put("$base/rulesets/${ruleset.identifier}") {
            contentType(ContentType.Application.Json)
            setBody(ruleset)
        }
    }

    suspend fun saveTeamPouleConfigs(configs: List<TeamPouleConfiguration>) {
        httpClient.post("$base/team_poule_configs/update") {
            contentType(ContentType.Application.Json)
            setBody(configs)
        }
    }
}
